import ExcelJS from "exceljs";
import { createEmptyQuestionDraft, validateQuestionDraft } from "./qmsQuizQuestionUtils";
import { buildTemplateSampleRows } from "./qmsQuestionTemplateSamples";

export const QUESTION_IMPORT_HEADERS = [
  "Question",
  "Option1",
  "Option2",
  "Option3",
  "Option4",
  "CorrectOption",
];

function normalizeHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

function parseCsvLine(line) {
  const cells = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      cells.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  cells.push(current.trim());
  return cells;
}

function rowObjectFromCells(cells, headerMap) {
  const get = (name) => cells[headerMap[normalizeHeader(name)]] ?? "";
  return {
    Question: get("Question"),
    Option1: get("Option1"),
    Option2: get("Option2"),
    Option3: get("Option3"),
    Option4: get("Option4"),
    CorrectOption: get("CorrectOption"),
  };
}

function draftFromImportRow(row, rowNumber) {
  const questionText = String(row.Question || "").trim();
  const correctRaw = String(row.CorrectOption ?? "").trim();
  const correctNum = parseInt(correctRaw, 10);

  if (!questionText) {
    return { error: `Row ${rowNumber}: Question is required.` };
  }

  if (!Number.isInteger(correctNum) || correctNum < 1 || correctNum > 4) {
    return {
      error: `Row ${rowNumber}: CorrectOption must be a number from 1 to 4.`,
    };
  }

  const optionTexts = [1, 2, 3, 4].map((n) =>
    String(row[`Option${n}`] || "").trim(),
  );
  const filledCount = optionTexts.filter(Boolean).length;

  if (filledCount < 2) {
    return { error: `Row ${rowNumber}: At least two options are required.` };
  }

  if (!optionTexts[correctNum - 1]) {
    return {
      error: `Row ${rowNumber}: CorrectOption points to an empty option.`,
    };
  }

  const draft = createEmptyQuestionDraft(`import-${rowNumber}`);
  draft.questionText = questionText;
  draft.options = optionTexts.map((text, idx) => ({
    SerialNumber: idx + 1,
    OptionText: text,
    IsCorrect: idx + 1 === correctNum,
  }));

  return { draft };
}

function parseWorksheetRows(worksheet) {
  const rows = [];
  const headerRow = worksheet.getRow(1);
  const headerMap = {};

  headerRow.eachCell((cell, colNumber) => {
    const key = normalizeHeader(cell.value);
    if (key) headerMap[key] = colNumber - 1;
  });

  const required = ["question", "option1", "option2", "correctoption"];
  const missing = required.filter((h) => headerMap[h] == null);
  if (missing.length) {
    throw new Error(
      "Invalid template. Required columns: Question, Option1, Option2, Option3, Option4, CorrectOption.",
    );
  }

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    const cells = [];
    for (let c = 1; c <= QUESTION_IMPORT_HEADERS.length; c += 1) {
      cells.push(String(row.getCell(c).text || row.getCell(c).value || "").trim());
    }
    if (cells.every((cell) => !cell)) return;
    rows.push(rowObjectFromCells(cells, headerMap));
  });

  return rows;
}

export async function downloadQuestionImportTemplate() {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Questions");

  ws.addRow(QUESTION_IMPORT_HEADERS);
  buildTemplateSampleRows(100).forEach((row) => ws.addRow(row));

  ws.getRow(1).eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF4472C4" },
    };
  });

  ws.columns = QUESTION_IMPORT_HEADERS.map((header) => ({
    header,
    key: header,
    width: header === "Question" ? 42 : Math.max(14, header.length + 4),
  }));

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = "QMS_Question_Import_Template.xlsx";
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function parseQuestionImportFile(file) {
  if (!file) {
    return { drafts: [], errors: ["Please choose a file to import."] };
  }

  const ext = String(file.name || "")
    .split(".")
    .pop()
    .toLowerCase();

  try {
    let rawRows = [];

    if (ext === "csv") {
      const text = await file.text();
      const lines = text
        .replace(/^\uFEFF/, "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

      if (lines.length < 2) {
        return { drafts: [], errors: ["CSV file has no data rows."] };
      }

      const headerCells = parseCsvLine(lines[0]).map(normalizeHeader);
      const headerMap = {};
      headerCells.forEach((key, idx) => {
        if (key) headerMap[key] = idx;
      });

      rawRows = lines.slice(1).map((line) => {
        const cells = parseCsvLine(line);
        return rowObjectFromCells(cells, headerMap);
      });
    } else if (ext === "xlsx" || ext === "xls") {
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(await file.arrayBuffer());
      const ws = wb.worksheets[0];
      if (!ws) {
        return { drafts: [], errors: ["Workbook has no worksheets."] };
      }
      rawRows = parseWorksheetRows(ws);
    } else {
      return {
        drafts: [],
        errors: ["Unsupported file type. Use .xlsx or .csv."],
      };
    }

    const drafts = [];
    const errors = [];

    rawRows.forEach((row, idx) => {
      const result = draftFromImportRow(row, idx + 2);
      if (result.error) errors.push(result.error);
      else if (result.draft) drafts.push(result.draft);
    });

    if (!drafts.length && !errors.length) {
      errors.push("No valid question rows found in the file.");
    }

    return { drafts, errors };
  } catch (error) {
    return {
      drafts: [],
      errors: [error?.message || "Could not read import file."],
    };
  }
}

export function getCorrectOptionNumber(draft) {
  const idx = (draft?.options || []).findIndex((o) => o.IsCorrect);
  return idx >= 0 ? idx + 1 : "";
}

export function setCorrectOptionNumber(draft, correctNum) {
  if (correctNum === null || correctNum === "" || correctNum === undefined) {
    return {
      ...draft,
      options: draft.options.map((o) => ({ ...o, IsCorrect: false })),
    };
  }

  const parsed = parseInt(correctNum, 10);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > 4) {
    return {
      ...draft,
      options: draft.options.map((o) => ({ ...o, IsCorrect: false })),
    };
  }

  return {
    ...draft,
    options: draft.options.map((o, i) => ({
      ...o,
      IsCorrect: i + 1 === parsed,
    })),
  };
}

export function validateImportDrafts(drafts) {
  const errorsById = {};
  (drafts || []).forEach((draft, idx) => {
    const messages = [];
    const fieldErrors = validateQuestionDraft(draft);
    if (fieldErrors.questionText) messages.push(fieldErrors.questionText);
    if (fieldErrors.options) messages.push(fieldErrors.options);
    if (fieldErrors.correct) messages.push(fieldErrors.correct);
    if (messages.length) {
      errorsById[draft.id] = `Row ${idx + 1}: ${messages.join(" ")}`;
    }
  });
  return errorsById;
}
